// Strata / MCST (Management Corporation Strata Title) financial-health score for
// a private unit. There is NO public dataset of condo fund balances — under the
// BMSMA these are owner-facing (AGM audited accounts) and obtainable per-sale via
// a §47 certificate. So every input here is user-supplied; the score is a
// transparent, heuristic read of the figures they bring, not market data.
//
// Decision support, not advice. Benchmarks are rules of thumb — facilities and
// building age move the "right" reserve level a lot.

import type { StrataInputs } from "../profile/schema";

const SQM_PER_SQFT = 10.7639;

export type StrataBand = "healthy" | "watch" | "underfunded" | "unknown";
export interface StrataFactor {
  label: string;
  verdict: "good" | "warn" | "bad" | "na";
  note: string;
}
export interface StrataResult {
  score: number; // 0-100 (0 when unknown)
  band: StrataBand;
  factors: StrataFactor[];
  reserveMonths: number | null; // sinking fund in months of total contributions
  feePsf: number | null; // monthly maintenance $psf
  provided: number; // number of scored (non-na) factors
}

const clamp = (x: number, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, x));
// Map x in [x0,x1] linearly to [y0,y1], clamped at the ends.
const lerp = (x: number, x0: number, x1: number, y0: number, y1: number) =>
  x <= x0 ? y0 : x >= x1 ? y1 : y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);

export function strataHealth(inp: StrataInputs & { areaSqm?: number }): StrataResult {
  const factors: StrataFactor[] = [];
  const scored: { w: number; s: number }[] = [];
  const add = (label: string, w: number, s: number | null, verdict: StrataFactor["verdict"], note: string) => {
    factors.push({ label, verdict, note });
    if (s != null) scored.push({ w, s });
  };

  const units = inp.units && inp.units > 0 ? inp.units : null;
  const monthly = inp.monthlyContribution && inp.monthlyContribution > 0 ? inp.monthlyContribution : null;
  const balance = inp.sinkingBalance && inp.sinkingBalance > 0 ? inp.sinkingBalance : null;

  // Reserve depth (main weight): sinking-fund balance expressed in months of the
  // whole development's monthly contributions. A rough but robust proxy from the
  // three figures a buyer can usually get. Conservative — the denominator also
  // includes the management portion, so it errs toward "thin".
  let reserveMonths: number | null = null;
  if (balance != null && monthly != null && units != null) {
    reserveMonths = balance / (monthly * units);
    const s = clamp(lerp(reserveMonths, 3, 24, 0, 100));
    const v = reserveMonths >= 18 ? "good" : reserveMonths >= 9 ? "warn" : "bad";
    add("Reserve depth", 0.4, s, v,
      `Sinking fund ≈ ${reserveMonths.toFixed(0)} months of total contributions` +
      (reserveMonths < 9 ? " — thin; budget for future special levies." : reserveMonths >= 18 ? " — healthy buffer." : " — moderate."));
  } else {
    add("Reserve depth", 0.4, null, "na", "Enter sinking-fund balance, monthly contribution and unit count to score.");
  }

  // Special-levy history.
  if (inp.specialLevy3y != null) {
    add("Special-levy history", 0.2, inp.specialLevy3y ? 40 : 100, inp.specialLevy3y ? "warn" : "good",
      inp.specialLevy3y
        ? "A special levy was raised in the last 3 years — check whether it topped up reserves (neutral) or covered a shortfall (a concern)."
        : "No special levy in the last 3 years.");
  } else {
    add("Special-levy history", 0.2, null, "na", "Ask about special levies in the last 3 years.");
  }

  // Building age vs reserves — older assets (lifts, façade, waterproofing) need
  // deeper reserves; old + thin is the worst combination.
  const age = inp.ageYears && inp.ageYears > 0 ? inp.ageYears : null;
  if (age != null) {
    let s = age <= 10 ? 90 : age <= 25 ? 70 : 45;
    if (reserveMonths != null && age > 20 && reserveMonths < 12) s = 25;
    add("Building age vs reserves", 0.15, s, s >= 70 ? "good" : s >= 45 ? "warn" : "bad",
      `~${Math.round(age)} yr-old development` + (age > 20 ? " — ageing assets drive larger capital works." : "."));
  } else {
    add("Building age vs reserves", 0.15, null, "na", "Older developments face larger capital works — note the building's age.");
  }

  // Arrears.
  if (inp.arrearsPct != null && inp.arrearsPct >= 0) {
    const s = clamp(lerp(inp.arrearsPct, 3, 20, 100, 0));
    add("Arrears", 0.15, s, inp.arrearsPct <= 5 ? "good" : inp.arrearsPct <= 12 ? "warn" : "bad",
      `${inp.arrearsPct.toFixed(0)}% in arrears` + (inp.arrearsPct > 12 ? " — cashflow risk." : "."));
  } else {
    add("Arrears", 0.15, null, "na", "Ask what share of contributions are in arrears (from the AGM).");
  }

  // Governance.
  if (inp.selfManaged != null) {
    const small = units != null && units < 30;
    const s = inp.selfManaged ? (small ? 55 : 70) : 85;
    add("Governance", 0.1, s, s >= 80 ? "good" : "warn",
      inp.selfManaged
        ? `Self-managed${small ? " small development — quality varies; review the accounts closely." : "."}`
        : "Professionally managed (managing agent).");
  } else {
    add("Governance", 0.1, null, "na", "Note whether it's self-managed or has a managing agent.");
  }

  // Maintenance fee reasonableness — informational only (unweighted): high fees
  // often just mean more facilities, so this is a flag, not a score.
  let feePsf: number | null = null;
  if (monthly != null && inp.areaSqm && inp.areaSqm > 0) {
    feePsf = monthly / (inp.areaSqm * SQM_PER_SQFT);
    const v = feePsf < 0.25 || feePsf > 0.75 ? "warn" : "good";
    add("Maintenance fee", 0, null, v,
      `≈ S$${feePsf.toFixed(2)} psf/month` +
      (feePsf < 0.25 ? " — low; verify the fund isn't underfunded." : feePsf > 0.75 ? " — high carrying cost (often more facilities)." : " — within a typical range."));
  }

  const provided = scored.length;
  if (provided < 2) return { score: 0, band: "unknown", factors, reserveMonths, feePsf, provided };
  const wsum = scored.reduce((a, b) => a + b.w, 0);
  const score = Math.round(scored.reduce((a, b) => a + b.w * b.s, 0) / wsum);
  const band: StrataBand = score >= 70 ? "healthy" : score >= 45 ? "watch" : "underfunded";
  return { score, band, factors, reserveMonths, feePsf, provided };
}

export const STRATA_LABEL: Record<StrataBand, string> = {
  healthy: "Healthy", watch: "Watch", underfunded: "Underfunded", unknown: "Not scored",
};
export function strataColour(band: StrataBand): string {
  return band === "healthy" ? "var(--good)" : band === "underfunded" ? "var(--bad)" : "var(--neutral)";
}
